/* GoogleCommunityMapView — premium live Google Maps view for the PawLink Community page.
 *
 * • Uses Google Maps Platform (via @vis.gl/react-google-maps — Google's official React lib).
 * • Real streets, real places, smooth WebGL vector tiles (Uber/Airbnb feel).
 * • AdvancedMarkers render custom HTML (pet photo + colored PawLink ring) for a Snap-Map feel.
 * • Data comes strictly from the DB (helpers / lost_pets / recent_found from /api/community/nearby).
 *   No fake activity ever.
 * • Marker click → parent's onSelectPet / onSelectHelper handler → existing PawLink modal.
 */
import { useEffect, useMemo, useState } from "react";
import {
    APIProvider,
    Map,
    AdvancedMarker,
    InfoWindow,
    useMap,
} from "@vis.gl/react-google-maps";
import { fileUrl } from "@/lib/api";

const PLACEHOLDER_DOG =
    "https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&w=200&q=60";

// Google's documented public demo Map ID — used only as fallback so AdvancedMarkers render.
// Users are guided to create their own Map ID in Google Cloud Console for a PawLink-branded style.
const FALLBACK_MAP_ID = "DEMO_MAP_ID";

const RING_COLORS = {
    emerald: "#10b981",  // helpers / guardians
    red: "#ef4444",      // lost pets
    amber: "#f59e0b",    // recently found
    forest: "#1e3a2f",   // you
};

/* ---- Custom HTML marker: circular photo with colored ring + optional pulse ---- */
function PetPhotoMarker({ photo, ring = "emerald", size = 46, pulse = false, badge = null }) {
    const color = RING_COLORS[ring] || RING_COLORS.emerald;
    const src = photo ? fileUrl(photo) : PLACEHOLDER_DOG;
    return (
        <div
            className="pl-gmap-pin"
            style={{
                width: size,
                height: size,
                position: "relative",
                filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.25))",
            }}
        >
            {pulse && (
                <span
                    aria-hidden
                    style={{
                        position: "absolute",
                        inset: -6,
                        borderRadius: "50%",
                        background: `${color}55`,
                        animation: "pl-gmap-pulse 1.6s ease-out infinite",
                    }}
                />
            )}
            <span
                aria-hidden
                style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    border: `3px solid ${color}`,
                    boxSizing: "border-box",
                    background: "white",
                }}
            />
            <img
                src={src}
                alt=""
                loading="lazy"
                onError={(e) => {
                    e.currentTarget.src = PLACEHOLDER_DOG;
                }}
                style={{
                    position: "absolute",
                    inset: 3,
                    width: `calc(100% - 6px)`,
                    height: `calc(100% - 6px)`,
                    borderRadius: "50%",
                    objectFit: "cover",
                    background: "#f4f4f4",
                }}
            />
            {badge && (
                <span
                    aria-hidden
                    style={{
                        position: "absolute",
                        bottom: -4,
                        right: -4,
                        minWidth: 20,
                        height: 20,
                        padding: "0 5px",
                        borderRadius: 999,
                        background: color,
                        color: "white",
                        fontSize: 11,
                        fontWeight: 800,
                        display: "grid",
                        placeItems: "center",
                        border: "2px solid white",
                        lineHeight: 1,
                    }}
                >
                    {badge}
                </span>
            )}
        </div>
    );
}

/* ---- Blue dot for user's own location ---- */
function SelfDotMarker() {
    return (
        <div style={{ position: "relative", width: 22, height: 22 }}>
            <span
                aria-hidden
                style={{
                    position: "absolute",
                    inset: -8,
                    borderRadius: "50%",
                    background: "rgba(66, 133, 244, 0.20)",
                    animation: "pl-gmap-pulse 2.2s ease-out infinite",
                }}
            />
            <span
                style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    background: "#4285F4",
                    border: "3px solid white",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                }}
            />
        </div>
    );
}

/* ---- Orange paw marker for pets currently on a walk ---- */
function WalkPawMarker({ photo, size = 42, pulse = true }) {
    const orange = "#F87A2A";
    const src = photo ? fileUrl(photo) : PLACEHOLDER_DOG;
    return (
        <div
            style={{
                width: size,
                height: size,
                position: "relative",
                filter: "drop-shadow(0 4px 10px rgba(232, 95, 30, 0.35))",
            }}
        >
            {pulse && (
                <span
                    aria-hidden
                    style={{
                        position: "absolute",
                        inset: -6,
                        borderRadius: "50%",
                        background: "rgba(248, 122, 42, 0.35)",
                        animation: "pl-gmap-pulse 1.8s ease-out infinite",
                    }}
                />
            )}
            <span
                aria-hidden
                style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    border: `3px solid ${orange}`,
                    boxSizing: "border-box",
                    background: "white",
                }}
            />
            <img
                src={src}
                alt=""
                loading="lazy"
                onError={(e) => { e.currentTarget.src = PLACEHOLDER_DOG; }}
                style={{
                    position: "absolute",
                    inset: 3,
                    width: `calc(100% - 6px)`,
                    height: `calc(100% - 6px)`,
                    borderRadius: "50%",
                    objectFit: "cover",
                    background: "#f4f4f4",
                }}
            />
            {/* Little paw badge */}
            <span
                aria-hidden
                style={{
                    position: "absolute",
                    bottom: -4,
                    right: -4,
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    background: orange,
                    display: "grid",
                    placeItems: "center",
                    border: "2px solid white",
                    fontSize: 11,
                    lineHeight: 1,
                }}
            >
                🐾
            </span>
        </div>
    );
}

/* ---- Small child of <Map> that reacts to user location changes and flies there ---- */
function FlyToUserLocation({ center, zoom = 15 }) {
    const map = useMap();
    useEffect(() => {
        if (map && center) {
            map.panTo({ lat: center.latitude, lng: center.longitude });
            const current = map.getZoom() ?? 0;
            if (current < zoom) map.setZoom(zoom);
        }
    }, [map, center, zoom]);
    return null;
}

export default function GoogleCommunityMapView({
    initialCenter,
    initialZoom = 4,
    userCenter,             // { latitude, longitude } | null — real DB-stored user coords
    helpers = [],
    lostPets = [],
    recentFound = [],
    walks = [],             // pins from /api/walks/nearby-pins (pre-fuzzed)
    filter = "all",
    onSelectPet,
    onSelectHelper,
    heightClass = "min(58vh, 620px)",
    t,
}) {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const mapId = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || FALLBACK_MAP_ID;

    // Info-window state for walk pin (compact, no PII).
    const [openWalkId, setOpenWalkId] = useState(null);
    // Force re-render every 30s so countdowns in the info-window stay fresh.
    const [, tick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => tick((x) => x + 1), 30000);
        return () => clearInterval(id);
    }, []);

    const showLost = filter === "all" || filter === "lost";
    const showHelpers = filter === "all" || filter === "helpers";
    const showFound = filter === "all" || filter === "found";
    const showWalks = filter === "all" || filter === "walking_now";

    // Sanitize + memoize marker lists so React only reconciles when data changes.
    const helperMarkers = useMemo(
        () => (showHelpers ? helpers.filter((h) => h?.latitude != null && h?.longitude != null) : []),
        [showHelpers, helpers],
    );
    const lostMarkers = useMemo(
        () => (showLost ? lostPets.filter((p) => p?.latitude != null && p?.longitude != null) : []),
        [showLost, lostPets],
    );
    const foundMarkers = useMemo(
        () => (showFound ? recentFound.filter((p) => p?.latitude != null && p?.longitude != null) : []),
        [showFound, recentFound],
    );
    const walkMarkers = useMemo(
        () => (showWalks ? walks.filter((w) => w?.lat != null && w?.lng != null) : []),
        [showWalks, walks],
    );

    if (!apiKey) {
        return (
            <div
                data-testid="community-gmap-missing-key"
                className="rounded-3xl border border-red-500/30 bg-red-500/10 p-6 text-sm text-red-700 dark:text-red-200"
                style={{ height: heightClass }}
            >
                <p className="font-semibold">Google Maps API key missing.</p>
                <p className="mt-1 opacity-80">
                    Add <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> to <code>/app/frontend/.env</code>
                    then restart the frontend.
                </p>
            </div>
        );
    }

    return (
        <div
            data-testid="community-gmap"
            style={{ height: heightClass, width: "100%", position: "relative" }}
        >
            <APIProvider apiKey={apiKey} libraries={["marker"]}>
                <Map
                    mapId={mapId}
                    defaultCenter={{ lat: initialCenter.latitude, lng: initialCenter.longitude }}
                    defaultZoom={initialZoom}
                    gestureHandling="greedy"
                    disableDefaultUI={false}
                    zoomControl
                    mapTypeControl={false}
                    streetViewControl={false}
                    fullscreenControl={false}
                    clickableIcons={false}
                    style={{ width: "100%", height: "100%" }}
                >
                    {/* Auto-pan to user's real location whenever it appears */}
                    <FlyToUserLocation center={userCenter} zoom={14} />

                    {/* You are here */}
                    {userCenter && (
                        <AdvancedMarker
                            position={{ lat: userCenter.latitude, lng: userCenter.longitude }}
                            title={t?.("community.you_are_here") || "You"}
                            zIndex={1000}
                        >
                            <SelfDotMarker />
                        </AdvancedMarker>
                    )}

                    {/* Guardians / helpers — emerald */}
                    {helperMarkers.map((h) => (
                        <AdvancedMarker
                            key={`h-${h.id}`}
                            position={{ lat: h.latitude, lng: h.longitude }}
                            onClick={() => onSelectHelper?.(h)}
                            title={h.owner_name || "PawLink Guardian"}
                        >
                            <PetPhotoMarker photo={h.pet_photo_path} ring="emerald" size={h.is_helper ? 48 : 40} />
                        </AdvancedMarker>
                    ))}

                    {/* Recently reunited — amber with ❤ badge */}
                    {foundMarkers.map((p) => (
                        <AdvancedMarker
                            key={`f-${p.public_id}`}
                            position={{ lat: p.latitude, lng: p.longitude }}
                            onClick={() => onSelectPet?.({ pet: p, kind: "found" })}
                            title={p.name}
                        >
                            <PetPhotoMarker photo={p.photo_path} ring="amber" size={46} badge="❤" />
                        </AdvancedMarker>
                    ))}

                    {/* Lost pets — red with pulse and ! badge (always on top) */}
                    {lostMarkers.map((p) => (
                        <AdvancedMarker
                            key={`l-${p.public_id}`}
                            position={{ lat: p.latitude, lng: p.longitude }}
                            onClick={() => onSelectPet?.({ pet: p, kind: "lost" })}
                            title={p.name}
                            zIndex={900}
                        >
                            <PetPhotoMarker photo={p.photo_path} ring="red" size={58} pulse badge="!" />
                        </AdvancedMarker>
                    ))}

                    {/* Walking Now — orange paw markers (pre-fuzzed coords from server) */}
                    {walkMarkers.map((w) => (
                        <AdvancedMarker
                            key={`w-${w.id}`}
                            position={{ lat: w.lat, lng: w.lng }}
                            onClick={() => setOpenWalkId(w.id)}
                            title={w.pet_first_name || "Walking now"}
                            zIndex={700}
                        >
                            <WalkPawMarker photo={w.pet_photo} size={44} />
                        </AdvancedMarker>
                    ))}

                    {/* Walking Now — compact info window (no PII, no exact coords) */}
                    {(() => {
                        const w = walkMarkers.find((x) => x.id === openWalkId);
                        if (!w) return null;
                        const mins = Math.max(0, Math.floor((w.remaining_seconds || 0) / 60));
                        return (
                            <InfoWindow
                                position={{ lat: w.lat, lng: w.lng }}
                                onCloseClick={() => setOpenWalkId(null)}
                                pixelOffset={[0, -46]}
                            >
                                <div
                                    data-testid={`walk-info-${w.id}`}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                        padding: "4px 6px 6px 4px",
                                        maxWidth: 240,
                                        fontFamily: "inherit",
                                    }}
                                >
                                    <img
                                        src={w.pet_photo ? fileUrl(w.pet_photo) : PLACEHOLDER_DOG}
                                        alt=""
                                        onError={(e) => { e.currentTarget.src = PLACEHOLDER_DOG; }}
                                        style={{
                                            width: 44, height: 44, borderRadius: "50%",
                                            objectFit: "cover",
                                            border: "2px solid #F87A2A",
                                            flexShrink: 0,
                                        }}
                                    />
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontWeight: 700, fontSize: 13, color: "#1e3a2f", lineHeight: 1.15 }}>
                                            {w.pet_first_name || (t?.("walks.friend") || "A pet")}
                                        </div>
                                        <div style={{ fontSize: 11, color: "#5b6b62", marginTop: 2, lineHeight: 1.2 }}>
                                            {typeof w.approx_distance_km === "number" && (
                                                <span>~{w.approx_distance_km} km · </span>
                                            )}
                                            <span>{mins}m {t?.("walks.left_short") || "left"}</span>
                                        </div>
                                        <div style={{ fontSize: 10, color: "#8a9791", marginTop: 3 }}>
                                            🔒 {t?.("community.sheet_privacy") || "Approximate area only"}
                                        </div>
                                    </div>
                                </div>
                            </InfoWindow>
                        );
                    })()}
                </Map>
            </APIProvider>
        </div>
    );
}
